import React, { useState, Fragment, Component } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { SmartphoneIcon, CameraIcon, CheckCircleIcon, XCircleIcon, InfoIcon, UploadIcon, ArrowRightIcon, BatteryIcon, MonitorIcon, WifiIcon, BluetoothIcon, RotateCcwIcon, ShieldIcon, AlertTriangleIcon, UserIcon, RefreshCwIcon, FileTextIcon, TrendingUpIcon, TrendingDownIcon, DollarSignIcon, Clock8Icon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import RechartsLineChart from '../components/charts/RechartsLineChart';
const steps = [{
  id: 1,
  name: 'Device Info'
}, {
  id: 2,
  name: 'Photo Upload'
}, {
  id: 3,
  name: 'AI Analysis'
}, {
  id: 4,
  name: 'Risk Assessment'
}, {
  id: 5,
  name: 'Results'
}];
const PhoneGrading = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [deviceInfo, setDeviceInfo] = useState({
    model: '',
    capacity: '',
    carrier: '',
    imei: ''
  });
  const [photos, setPhotos] = useState<string[]>([]);
  const [analysisComplete, setAnalysisComplete] = useState(false);
  const [riskAssessmentComplete, setRiskAssessmentComplete] = useState(false);
  const [riskScore, setRiskScore] = useState(47.25);
  const [riskCategory, setRiskCategory] = useState('MEDIUM');
  const [riskData, setRiskData] = useState({
    overall_risk_score: 47.25,
    risk_category: 'MEDIUM',
    threshold: {
      low: 30,
      medium: 60,
      high: 85
    },
    component_scores: {
      supplier_risk: 38.4,
      device_verification: 52.7,
      device_authenticity: 25.3,
      transaction_risk: 61.5,
      compliance_risk: 42.8
    },
    weighted_scores: {
      supplier_risk: 9.6,
      device_verification: 15.81,
      device_authenticity: 5.06,
      transaction_risk: 9.23,
      compliance_risk: 4.28
    },
    industry_benchmarks: {
      supplier_risk: 25.0,
      device_verification: 40.0,
      device_authenticity: 22.0,
      transaction_risk: 45.0,
      compliance_risk: 30.0
    },
    historical_trends: [{
      date: 'Feb',
      value: 52.8
    }, {
      date: 'Mar',
      value: 54.3
    }, {
      date: 'Apr',
      value: 51.2
    }, {
      date: 'May',
      value: 49.6
    }, {
      date: 'Jun',
      value: 48.1
    }, {
      date: 'Jul',
      value: 47.25
    }],
    recommendation: {
      action: 'ADDITIONAL_VERIFICATION',
      details: 'Proceed with additional verification of highest risk components.',
      required_approvals: ['verification_team'],
      specific_actions: [{
        component: 'transaction_risk',
        action: 'Verify payment method and buyer history',
        severity: 'high'
      }, {
        component: 'device_verification',
        action: 'Run extended diagnostics on device connectivity',
        severity: 'medium'
      }, {
        component: 'compliance_risk',
        action: 'Confirm export eligibility for destination market',
        severity: 'low'
      }]
    },
    financial_impact: {
      estimated_cost: 120,
      potential_loss: 410,
      roi: 241.7
    }
  });
  const supplierInfo = {
    name: 'TechTrade Solutions',
    relationship_length: '1.8 years',
    transaction_count: 284,
    return_rate: '3.2%',
    average_quality: 'B+',
    payment_terms: 'Net 30',
    location: 'Miami, FL',
    verification_status: 'Verified'
  };
  const handleNextStep = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
      if (currentStep === 3) {
        setTimeout(() => {
          setAnalysisComplete(true);
        }, 2000);
      }
      if (currentStep === 4) {
        setTimeout(() => {
          setRiskAssessmentComplete(true);
        }, 2000);
      }
    }
  };
  const handlePreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };
  const handleDeviceInfoChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setDeviceInfo({
      ...deviceInfo,
      [e.target.name]: e.target.value
    });
  };
  const handleAddPhoto = () => {
    setPhotos([...photos, `https://source.unsplash.com/random/400x300?smartphone&sig=${Math.random()}`]);
  };
  const getRiskColor = (score, type = 'bg') => {
    if (score <= 30) return type === 'bg' ? 'bg-green-100 text-green-800' : 'text-green-600';
    if (score <= 60) return type === 'bg' ? 'bg-yellow-100 text-yellow-800' : 'text-yellow-600';
    if (score <= 85) return type === 'bg' ? 'bg-orange-100 text-orange-800' : 'text-orange-600';
    return type === 'bg' ? 'bg-red-100 text-red-800' : 'text-red-600';
  };
  const getRiskBadgeVariant = category => {
    switch (category) {
      case 'LOW':
        return 'success';
      case 'MEDIUM':
        return 'warning';
      case 'HIGH':
        return 'destructive';
      case 'CRITICAL':
        return 'destructive';
      default:
        return 'default';
    }
  };
  const getActionStyle = action => {
    switch (action) {
      case 'PROCEED':
        return 'bg-green-500 text-white';
      case 'ADDITIONAL_VERIFICATION':
        return 'bg-yellow-500 text-white';
      case 'MANUAL_REVIEW':
        return 'bg-orange-500 text-white';
      case 'REJECT':
        return 'bg-red-500 text-white';
      default:
        return 'bg-gray-500 text-white';
    }
  };
  const getRiskComponentIcon = component => {
    switch (component) {
      case 'supplier_risk':
        return <UserIcon className="h-4 w-4" data-id="element-1336" />;
      case 'device_verification':
        return <ShieldIcon className="h-4 w-4" data-id="element-1337" />;
      case 'device_authenticity':
        return <CheckCircleIcon className="h-4 w-4" data-id="element-1338" />;
      case 'transaction_risk':
        return <RefreshCwIcon className="h-4 w-4" data-id="element-1339" />;
      case 'compliance_risk':
        return <FileTextIcon className="h-4 w-4" data-id="element-1340" />;
      default:
        return <AlertCircleIcon className="h-4 w-4" data-id="element-1341" />;
    }
  };
  const getTrendIndicator = (current, benchmark) => {
    if (current > benchmark) {
      return <span className="flex items-center text-red-500 text-xs ml-1" data-id="element-1342">
          <TrendingUpIcon className="h-3 w-3 mr-1" data-id="element-1343" />
          {((current - benchmark) / benchmark * 100).toFixed(1)}%
        </span>;
    } else {
      return <span className="flex items-center text-green-500 text-xs ml-1" data-id="element-1344">
          <TrendingDownIcon className="h-3 w-3 mr-1" data-id="element-1345" />
          {((benchmark - current) / benchmark * 100).toFixed(1)}%
        </span>;
    }
  };
  const getSeverityBadge = severity => {
    switch (severity) {
      case 'high':
        return <Badge variant="destructive" data-id="element-1346">High</Badge>;
      case 'medium':
        return <Badge variant="warning" data-id="element-1347">Medium</Badge>;
      case 'low':
        return <Badge variant="success" data-id="element-1348">Low</Badge>;
      default:
        return <Badge data-id="element-1349">Unknown</Badge>;
    }
  };
  return <div className="space-y-6" data-id="element-1350">
      <div className="flex items-center justify-between" data-id="element-1351">
        <h1 className="text-2xl font-bold" data-id="element-1352">AI Grading</h1>
        <Button variant="outline" data-id="element-1353">
          <RotateCcwIcon className="mr-2 h-4 w-4" data-id="element-1354" />
          Start New Grading
        </Button>
      </div>
      <Card data-id="element-1355">
        <CardHeader data-id="element-1356">
          <div className="flex items-center justify-between" data-id="element-1357">
            <div data-id="element-1358">
              <CardTitle data-id="element-1359">Phone Condition Grading</CardTitle>
              <CardDescription data-id="element-1360">
                AI-powered device assessment for accurate market value
              </CardDescription>
            </div>
            <SmartphoneIcon className="h-5 w-5 text-muted-foreground" data-id="element-1361" />
          </div>
        </CardHeader>
        <CardContent data-id="element-1362">
          <div className="flex items-center justify-between mb-8" data-id="element-1363">
            {steps.map((step, index) => <Fragment key={step.id} data-id="element-1364">
                <div className="flex flex-col items-center" data-id="element-1365">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${step.id < currentStep ? 'bg-primary border-primary text-primary-foreground' : step.id === currentStep ? 'border-primary text-primary' : 'border-muted-foreground text-muted-foreground'}`} data-id="element-1366">
                    {step.id < currentStep ? <CheckCircleIcon className="h-5 w-5" data-id="element-1367" /> : step.id}
                  </div>
                  <span className={`mt-2 text-sm ${step.id === currentStep ? 'font-medium' : 'text-muted-foreground'}`} data-id="element-1368">
                    {step.name}
                  </span>
                </div>
                {index < steps.length - 1 && <div className={`flex-1 h-0.5 ${step.id < currentStep ? 'bg-primary' : 'bg-muted'}`} data-id="element-1369" />}
              </Fragment>)}
          </div>
          {currentStep === 1 && <div className="space-y-4" data-id="element-1370">
              <h3 className="text-lg font-medium" data-id="element-1371">Enter Device Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1372">
                <div className="space-y-2" data-id="element-1373">
                  <label htmlFor="model" className="text-sm font-medium" data-id="element-1374">
                    Phone Model
                  </label>
                  <select id="model" name="model" className="w-full p-2 rounded-md border border-input" value={deviceInfo.model} onChange={handleDeviceInfoChange} data-id="element-1375">
                    <option value="" data-id="element-1376">Select Model</option>
                    <option value="iphone13" data-id="element-1377">iPhone 13</option>
                    <option value="iphone13pro" data-id="element-1378">iPhone 13 Pro</option>
                    <option value="iphone13promax" data-id="element-1379">iPhone 13 Pro Max</option>
                    <option value="iphone12" data-id="element-1380">iPhone 12</option>
                    <option value="samsungs22" data-id="element-1381">Samsung Galaxy S22</option>
                    <option value="samsungs22ultra" data-id="element-1382">
                      Samsung Galaxy S22 Ultra
                    </option>
                    <option value="pixel6" data-id="element-1383">Google Pixel 6</option>
                  </select>
                </div>
                <div className="space-y-2" data-id="element-1384">
                  <label htmlFor="capacity" className="text-sm font-medium" data-id="element-1385">
                    Storage Capacity
                  </label>
                  <select id="capacity" name="capacity" className="w-full p-2 rounded-md border border-input" value={deviceInfo.capacity} onChange={handleDeviceInfoChange} data-id="element-1386">
                    <option value="" data-id="element-1387">Select Capacity</option>
                    <option value="64" data-id="element-1388">64 GB</option>
                    <option value="128" data-id="element-1389">128 GB</option>
                    <option value="256" data-id="element-1390">256 GB</option>
                    <option value="512" data-id="element-1391">512 GB</option>
                    <option value="1tb" data-id="element-1392">1 TB</option>
                  </select>
                </div>
                <div className="space-y-2" data-id="element-1393">
                  <label htmlFor="carrier" className="text-sm font-medium" data-id="element-1394">
                    Carrier
                  </label>
                  <select id="carrier" name="carrier" className="w-full p-2 rounded-md border border-input" value={deviceInfo.carrier} onChange={handleDeviceInfoChange} data-id="element-1395">
                    <option value="" data-id="element-1396">Select Carrier</option>
                    <option value="unlocked" data-id="element-1397">Unlocked</option>
                    <option value="att" data-id="element-1398">AT&T</option>
                    <option value="verizon" data-id="element-1399">Verizon</option>
                    <option value="tmobile" data-id="element-1400">T-Mobile</option>
                    <option value="sprint" data-id="element-1401">Sprint</option>
                  </select>
                </div>
                <div className="space-y-2" data-id="element-1402">
                  <label htmlFor="imei" className="text-sm font-medium" data-id="element-1403">
                    IMEI Number
                  </label>
                  <input id="imei" name="imei" type="text" className="w-full p-2 rounded-md border border-input" placeholder="Enter IMEI" value={deviceInfo.imei} onChange={handleDeviceInfoChange} data-id="element-1404" />
                </div>
              </div>
              <div className="mt-6 pt-6 border-t" data-id="element-1405">
                <h3 className="text-lg font-medium mb-4" data-id="element-1406">
                  Supplier Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1407">
                  <div className="space-y-2" data-id="element-1408">
                    <label htmlFor="supplier" className="text-sm font-medium" data-id="element-1409">
                      Supplier Name
                    </label>
                    <select id="supplier" name="supplier" className="w-full p-2 rounded-md border border-input" defaultValue="techTrade" data-id="element-1410">
                      <option value="" data-id="element-1411">Select Supplier</option>
                      <option value="techTrade" data-id="element-1412">TechTrade Solutions</option>
                      <option value="phoneWholesale" data-id="element-1413">
                        Phone Wholesale Inc
                      </option>
                      <option value="mobileSource" data-id="element-1414">Mobile Source LLC</option>
                    </select>
                  </div>
                  <div className="space-y-2" data-id="element-1415">
                    <label htmlFor="transaction_type" className="text-sm font-medium" data-id="element-1416">
                      Transaction Type
                    </label>
                    <select id="transaction_type" name="transaction_type" className="w-full p-2 rounded-md border border-input" defaultValue="wholesale" data-id="element-1417">
                      <option value="wholesale" data-id="element-1418">Wholesale Purchase</option>
                      <option value="consignment" data-id="element-1419">Consignment</option>
                      <option value="trade_in" data-id="element-1420">Trade-In</option>
                      <option value="direct" data-id="element-1421">Direct Purchase</option>
                    </select>
                  </div>
                  <div className="space-y-2" data-id="element-1422">
                    <label htmlFor="purchase_price" className="text-sm font-medium" data-id="element-1423">
                      Purchase Price
                    </label>
                    <input id="purchase_price" name="purchase_price" type="text" className="w-full p-2 rounded-md border border-input" placeholder="Enter price" defaultValue="410" data-id="element-1424" />
                  </div>
                  <div className="space-y-2" data-id="element-1425">
                    <label htmlFor="payment_method" className="text-sm font-medium" data-id="element-1426">
                      Payment Method
                    </label>
                    <select id="payment_method" name="payment_method" className="w-full p-2 rounded-md border border-input" defaultValue="credit" data-id="element-1427">
                      <option value="credit" data-id="element-1428">Credit (Net 30)</option>
                      <option value="wire" data-id="element-1429">Wire Transfer</option>
                      <option value="check" data-id="element-1430">Check</option>
                      <option value="cash" data-id="element-1431">Cash</option>
                    </select>
                  </div>
                </div>
                <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-md border border-blue-200 dark:border-blue-800" data-id="element-1432">
                  <div className="flex items-start" data-id="element-1433">
                    <InfoIcon className="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-1434" />
                    <div data-id="element-1435">
                      <p className="text-sm font-medium" data-id="element-1436">
                        Supplier Profile: TechTrade Solutions
                      </p>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2 text-xs" data-id="element-1437">
                        <div className="flex justify-between" data-id="element-1438">
                          <span className="text-muted-foreground" data-id="element-1439">
                            Relationship:
                          </span>
                          <span data-id="element-1440">1.8 years</span>
                        </div>
                        <div className="flex justify-between" data-id="element-1441">
                          <span className="text-muted-foreground" data-id="element-1442">
                            Transactions:
                          </span>
                          <span data-id="element-1443">284</span>
                        </div>
                        <div className="flex justify-between" data-id="element-1444">
                          <span className="text-muted-foreground" data-id="element-1445">
                            Return Rate:
                          </span>
                          <span className="flex items-center" data-id="element-1446">
                            3.2%
                            <span className="text-red-500 ml-1" data-id="element-1447">(+0.4%)</span>
                          </span>
                        </div>
                        <div className="flex justify-between" data-id="element-1448">
                          <span className="text-muted-foreground" data-id="element-1449">
                            Avg Quality:
                          </span>
                          <span data-id="element-1450">B+</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>}
          {currentStep === 2 && <div className="space-y-4" data-id="element-1451">
              <h3 className="text-lg font-medium" data-id="element-1452">Upload Device Photos</h3>
              <p className="text-sm text-muted-foreground mb-4" data-id="element-1453">
                Take clear photos of your device from all angles for accurate AI
                assessment.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4" data-id="element-1454">
                {photos.map((photo, index) => <div key={index} className="relative border rounded-md overflow-hidden h-40" data-id="element-1455">
                    <img src={photo} alt={`Device photo ${index + 1}`} className="w-full h-full object-cover" data-id="element-1456" />
                    <button className="absolute top-2 right-2 bg-black/50 text-white rounded-full p-1" data-id="element-1457">
                      <XCircleIcon className="h-4 w-4" data-id="element-1458" />
                    </button>
                  </div>)}
                <button onClick={handleAddPhoto} className="border border-dashed rounded-md h-40 flex flex-col items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors" data-id="element-1459">
                  <CameraIcon className="h-8 w-8 mb-2" data-id="element-1460" />
                  <span className="text-sm font-medium" data-id="element-1461">Add Photo</span>
                </button>
              </div>
              <div className="bg-muted/50 p-4 rounded-md mt-4" data-id="element-1462">
                <div className="flex items-start" data-id="element-1463">
                  <InfoIcon className="h-5 w-5 text-blue-500 mr-2 mt-0.5" data-id="element-1464" />
                  <div className="text-sm" data-id="element-1465">
                    <p className="font-medium" data-id="element-1466">Required photos:</p>
                    <ul className="mt-2 space-y-1 list-disc list-inside text-muted-foreground" data-id="element-1467">
                      <li data-id="element-1468">Front screen (powered on if possible)</li>
                      <li data-id="element-1469">Back panel</li>
                      <li data-id="element-1470">All four sides</li>
                      <li data-id="element-1471">Close-ups of any damage</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>}
          {currentStep === 3 && <div className="space-y-4" data-id="element-1472">
              <h3 className="text-lg font-medium" data-id="element-1473">AI Analysis In Progress</h3>
              <p className="text-sm text-muted-foreground mb-4" data-id="element-1474">
                Our AI is analyzing your device photos to determine condition
                and market value.
              </p>
              <div className="space-y-4" data-id="element-1475">
                <div className="bg-muted/30 rounded-md p-6" data-id="element-1476">
                  <div className="flex flex-col items-center justify-center" data-id="element-1477">
                    {!analysisComplete ? <>
                        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4" data-id="element-1478"></div>
                        <p className="text-sm font-medium" data-id="element-1479">
                          Processing Images...
                        </p>
                        <p className="text-xs text-muted-foreground mt-1" data-id="element-1480">
                          This may take a few moments
                        </p>
                      </> : <>
                        <CheckCircleIcon className="h-16 w-16 text-green-500 mb-4" data-id="element-1481" />
                        <p className="text-sm font-medium" data-id="element-1482">
                          Analysis Complete!
                        </p>
                        <p className="text-xs text-muted-foreground mt-1" data-id="element-1483">
                          Click "Next" to proceed with risk assessment
                        </p>
                      </>}
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-1484">
                  <div className={`p-4 border rounded-md ${analysisComplete ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' : 'border-muted bg-muted/30'}`} data-id="element-1485">
                    <div className="flex flex-col items-center text-center" data-id="element-1486">
                      <MonitorIcon className={`h-6 w-6 mb-2 ${analysisComplete ? 'text-green-500' : 'text-muted-foreground'}`} data-id="element-1487" />
                      <span className="text-sm font-medium" data-id="element-1488">Screen Check</span>
                      {analysisComplete && <span className="text-xs text-green-600 dark:text-green-400 mt-1" data-id="element-1489">
                          Passed
                        </span>}
                    </div>
                  </div>
                  <div className={`p-4 border rounded-md ${analysisComplete ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' : 'border-muted bg-muted/30'}`} data-id="element-1490">
                    <div className="flex flex-col items-center text-center" data-id="element-1491">
                      <BatteryIcon className={`h-6 w-6 mb-2 ${analysisComplete ? 'text-green-500' : 'text-muted-foreground'}`} data-id="element-1492" />
                      <span className="text-sm font-medium" data-id="element-1493">
                        Battery Health
                      </span>
                      {analysisComplete && <span className="text-xs text-green-600 dark:text-green-400 mt-1" data-id="element-1494">
                          89% - Good
                        </span>}
                    </div>
                  </div>
                  <div className={`p-4 border rounded-md ${analysisComplete ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' : 'border-muted bg-muted/30'}`} data-id="element-1495">
                    <div className="flex flex-col items-center text-center" data-id="element-1496">
                      <WifiIcon className={`h-6 w-6 mb-2 ${analysisComplete ? 'text-red-500' : 'text-muted-foreground'}`} data-id="element-1497" />
                      <span className="text-sm font-medium" data-id="element-1498">Connectivity</span>
                      {analysisComplete && <span className="text-xs text-red-600 dark:text-red-400 mt-1" data-id="element-1499">
                          Issues Detected
                        </span>}
                    </div>
                  </div>
                  <div className={`p-4 border rounded-md ${analysisComplete ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' : 'border-muted bg-muted/30'}`} data-id="element-1500">
                    <div className="flex flex-col items-center text-center" data-id="element-1501">
                      <BluetoothIcon className={`h-6 w-6 mb-2 ${analysisComplete ? 'text-green-500' : 'text-muted-foreground'}`} data-id="element-1502" />
                      <span className="text-sm font-medium" data-id="element-1503">Cosmetic</span>
                      {analysisComplete && <span className="text-xs text-green-600 dark:text-green-400 mt-1" data-id="element-1504">
                          Minor Wear
                        </span>}
                    </div>
                  </div>
                </div>
              </div>
            </div>}
          {currentStep === 4 && <div className="space-y-4" data-id="element-1505">
              <h3 className="text-lg font-medium" data-id="element-1506">Risk Assessment</h3>
              <p className="text-sm text-muted-foreground mb-4" data-id="element-1507">
                Evaluating potential risks associated with this device before
                proceeding with acquisition.
              </p>
              {!riskAssessmentComplete ? <div className="bg-muted/30 rounded-md p-6" data-id="element-1508">
                  <div className="flex flex-col items-center justify-center" data-id="element-1509">
                    <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4" data-id="element-1510"></div>
                    <p className="text-sm font-medium" data-id="element-1511">
                      Analyzing Risk Factors...
                    </p>
                    <p className="text-xs text-muted-foreground mt-1" data-id="element-1512">
                      Checking supplier history, device authenticity, and market
                      conditions
                    </p>
                  </div>
                </div> : <div className="space-y-6" data-id="element-1513">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1514">
                    <div className="border rounded-md p-4" data-id="element-1515">
                      <h4 className="text-sm font-medium mb-3" data-id="element-1516">
                        Overall Risk Score
                      </h4>
                      <div className="flex items-center justify-center" data-id="element-1517">
                        <div className={`w-24 h-24 rounded-full flex items-center justify-center border-4 ${getRiskColor(riskData.overall_risk_score)}`} data-id="element-1518">
                          <div className="flex flex-col items-center" data-id="element-1519">
                            <span className="text-2xl font-bold" data-id="element-1520">
                              {riskData.overall_risk_score}
                            </span>
                            <span className="text-xs mt-1" data-id="element-1521">
                              {riskData.risk_category}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="mt-3 text-center" data-id="element-1522">
                        <Badge variant={getRiskBadgeVariant(riskData.risk_category)} data-id="element-1523">
                          {riskData.risk_category}
                        </Badge>
                      </div>
                    </div>
                    <div className="border rounded-md p-4" data-id="element-1524">
                      <h4 className="text-sm font-medium mb-3" data-id="element-1525">
                        Recommended Action
                      </h4>
                      <div className={`p-3 rounded ${getActionStyle(riskData.recommendation.action)}`} data-id="element-1526">
                        <div className="text-base font-medium mb-1" data-id="element-1527">
                          {riskData.recommendation.action.replace('_', ' ')}
                        </div>
                        <p className="text-xs" data-id="element-1528">
                          {riskData.recommendation.details}
                        </p>
                      </div>
                      <div className="mt-3" data-id="element-1529">
                        <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded border border-red-200 dark:border-red-800 flex items-start" data-id="element-1530">
                          <AlertTriangleIcon className="h-4 w-4 text-red-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-1531" />
                          <div data-id="element-1532">
                            <p className="text-xs font-medium" data-id="element-1533">
                              {riskData.recommendation.specific_actions[0].action}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="border rounded-md p-4" data-id="element-1534">
                      <h4 className="text-sm font-medium mb-3" data-id="element-1535">
                        Financial Impact
                      </h4>
                      <div className="space-y-3" data-id="element-1536">
                        <div className="flex justify-between items-center pb-2 border-b" data-id="element-1537">
                          <span className="text-xs text-muted-foreground" data-id="element-1538">
                            Verification Cost
                          </span>
                          <span className="font-medium" data-id="element-1539">
                            ${riskData.financial_impact.estimated_cost}
                          </span>
                        </div>
                        <div className="flex justify-between items-center pb-2 border-b" data-id="element-1540">
                          <span className="text-xs text-muted-foreground" data-id="element-1541">
                            Potential Loss Prevention
                          </span>
                          <span className="font-medium text-green-600" data-id="element-1542">
                            ${riskData.financial_impact.potential_loss}
                          </span>
                        </div>
                        <div className="flex justify-between items-center" data-id="element-1543">
                          <span className="text-xs text-muted-foreground" data-id="element-1544">
                            ROI of Verification
                          </span>
                          <span className="font-medium text-green-600" data-id="element-1545">
                            {riskData.financial_impact.roi}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="border rounded-md p-4" data-id="element-1546">
                    <h4 className="text-sm font-medium mb-3" data-id="element-1547">
                      Risk Component Breakdown
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-id="element-1548">
                      {Object.entries(riskData.component_scores).map(([key, value]) => {
                  const benchmark = riskData.industry_benchmarks[key];
                  return <div key={key} className="flex flex-col items-center" data-id="element-1549">
                              <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-2 border-2 ${getRiskColor(value)}`} data-id="element-1550">
                                <div className="flex flex-col items-center" data-id="element-1551">
                                  {getRiskComponentIcon(key)}
                                  <span className="text-xs font-bold mt-1" data-id="element-1552">
                                    {value}
                                  </span>
                                </div>
                              </div>
                              <span className="text-xs text-center capitalize" data-id="element-1553">
                                {key.replace('_', ' ')}
                              </span>
                              <div className="flex items-center text-xs text-muted-foreground" data-id="element-1554">
                                <span data-id="element-1555">Ind: {benchmark}</span>
                                {getTrendIndicator(value, benchmark)}
                              </div>
                            </div>;
                })}
                    </div>
                    <div className="mt-4" data-id="element-1556">
                      <p className="text-xs font-medium mb-2" data-id="element-1557">
                        Risk Score Contribution
                      </p>
                      <div className="h-4 flex rounded-md overflow-hidden" data-id="element-1558">
                        {Object.entries(riskData.weighted_scores).map(([key, value]) => {
                    const score = riskData.component_scores[key];
                    return <div key={key} className={`${getRiskColor(score)}`} style={{
                      width: `${value / riskData.overall_risk_score * 100}%`
                    }} title={`${key.replace('_', ' ')}: ${value} points (${(value / riskData.overall_risk_score * 100).toFixed(1)}%)`} data-id="element-1559" />;
                  })}
                      </div>
                    </div>
                  </div>
                  <div className="border rounded-md p-4" data-id="element-1560">
                    <h4 className="text-sm font-medium mb-3" data-id="element-1561">
                      Risk Score History
                    </h4>
                    <div className="h-40" data-id="element-1562">
                      <RechartsLineChart data={riskData.historical_trends} height={150} color="#3b82f6" yAxisFormatter={value => `${value}`} tooltipFormatter={value => [`${value}`, 'Risk Score']} data-id="element-1563" />
                    </div>
                  </div>
                  <div className="border rounded-md p-4" data-id="element-1564">
                    <h4 className="text-sm font-medium mb-3" data-id="element-1565">
                      Specific Risk Factors
                    </h4>
                    <div className="space-y-3" data-id="element-1566">
                      {riskData.recommendation.specific_actions.map((action, index) => <div key={index} className="flex items-start p-3 border rounded-md" data-id="element-1567">
                            <div className="flex-shrink-0 mr-3 mt-1" data-id="element-1568">
                              {action.severity === 'high' ? <AlertTriangleIcon className="h-4 w-4 text-red-500" data-id="element-1569" /> : action.severity === 'medium' ? <AlertCircleIcon className="h-4 w-4 text-yellow-500" data-id="element-1570" /> : <InfoIcon className="h-4 w-4 text-blue-500" data-id="element-1571" />}
                            </div>
                            <div className="flex-1" data-id="element-1572">
                              <div className="flex items-center mb-1" data-id="element-1573">
                                <h3 className="text-sm font-medium" data-id="element-1574">
                                  {action.action}
                                </h3>
                                <div className="ml-2" data-id="element-1575">
                                  {getSeverityBadge(action.severity)}
                                </div>
                              </div>
                              <p className="text-xs text-muted-foreground" data-id="element-1576">
                                Related to {action.component.replace('_', ' ')}{' '}
                                score of{' '}
                                {riskData.component_scores[action.component]}
                              </p>
                            </div>
                          </div>)}
                    </div>
                  </div>
                </div>}
            </div>}
          {currentStep === 5 && <div className="space-y-6" data-id="element-1577">
              <h3 className="text-lg font-medium" data-id="element-1578">Final Assessment Results</h3>
              <div className="flex flex-col md:flex-row gap-6" data-id="element-1579">
                <div className="flex-1" data-id="element-1580">
                  <div className="border rounded-md overflow-hidden" data-id="element-1581">
                    <div className="bg-muted p-4 border-b" data-id="element-1582">
                      <h4 className="font-medium" data-id="element-1583">Device Assessment</h4>
                    </div>
                    <div className="p-6 space-y-4" data-id="element-1584">
                      <div className="flex justify-between items-center pb-2 border-b" data-id="element-1585">
                        <span className="text-sm text-muted-foreground" data-id="element-1586">
                          Overall Grade
                        </span>
                        <span className="text-lg font-bold" data-id="element-1587">B Grade</span>
                      </div>
                      <div className="flex justify-between items-center pb-2 border-b" data-id="element-1588">
                        <span className="text-sm text-muted-foreground" data-id="element-1589">
                          Estimated Value
                        </span>
                        <span className="text-lg font-bold" data-id="element-1590">$410 - $450</span>
                      </div>
                      <div className="flex justify-between items-center pb-2 border-b" data-id="element-1591">
                        <span className="text-sm text-muted-foreground" data-id="element-1592">
                          Functionality
                        </span>
                        <Badge variant="warning" data-id="element-1593">Minor Issues</Badge>
                      </div>
                      <div className="flex justify-between items-center pb-2 border-b" data-id="element-1594">
                        <span className="text-sm text-muted-foreground" data-id="element-1595">
                          Cosmetic Condition
                        </span>
                        <Badge variant="success" data-id="element-1596">Good</Badge>
                      </div>
                      <div className="flex justify-between items-center pb-2 border-b" data-id="element-1597">
                        <span className="text-sm text-muted-foreground" data-id="element-1598">
                          Market Demand
                        </span>
                        <Badge variant="success" data-id="element-1599">High</Badge>
                      </div>
                      <div className="flex justify-between items-center" data-id="element-1600">
                        <span className="text-sm text-muted-foreground" data-id="element-1601">
                          Risk Assessment
                        </span>
                        <Badge variant={getRiskBadgeVariant(riskData.risk_category)} data-id="element-1602">
                          {riskData.risk_category} (
                          {riskData.overall_risk_score})
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex-1" data-id="element-1603">
                  <div className="border rounded-md overflow-hidden" data-id="element-1604">
                    <div className="bg-muted p-4 border-b" data-id="element-1605">
                      <h4 className="font-medium" data-id="element-1606">Issues & Recommendations</h4>
                    </div>
                    <div className="p-4" data-id="element-1607">
                      <ul className="space-y-3" data-id="element-1608">
                        <li className="flex items-start" data-id="element-1609">
                          <XCircleIcon className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-1610" />
                          <div data-id="element-1611">
                            <p className="text-sm font-medium" data-id="element-1612">
                              Wi-Fi Connectivity Issues
                            </p>
                            <p className="text-xs text-muted-foreground" data-id="element-1613">
                              Device shows intermittent Wi-Fi connection
                              problems
                            </p>
                          </div>
                        </li>
                        <li className="flex items-start" data-id="element-1614">
                          <XCircleIcon className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-1615" />
                          <div data-id="element-1616">
                            <p className="text-sm font-medium" data-id="element-1617">
                              Minor Screen Scratches
                            </p>
                            <p className="text-xs text-muted-foreground" data-id="element-1618">
                              Light scratches visible on screen under bright
                              light
                            </p>
                          </div>
                        </li>
                        <li className="flex items-start" data-id="element-1619">
                          <AlertTriangleIcon className="h-5 w-5 text-yellow-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-1620" />
                          <div data-id="element-1621">
                            <p className="text-sm font-medium" data-id="element-1622">
                              Transaction Risk:{' '}
                              {riskData.component_scores.transaction_risk}
                            </p>
                            <p className="text-xs text-muted-foreground" data-id="element-1623">
                              {riskData.recommendation.specific_actions[0].action}
                            </p>
                          </div>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-md border border-blue-200 dark:border-blue-800" data-id="element-1624">
                    <div className="flex items-start" data-id="element-1625">
                      <InfoIcon className="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-1626" />
                      <div data-id="element-1627">
                        <p className="text-sm font-medium" data-id="element-1628">
                          Acquisition Recommendation
                        </p>
                        <p className="text-xs text-muted-foreground mt-1" data-id="element-1629">
                          Based on the condition assessment and risk profile, we
                          recommend proceeding with acquisition at $410 after
                          verifying payment method and buyer history.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-md border border-yellow-200 dark:border-yellow-800 mt-4" data-id="element-1630">
                <div className="flex items-start" data-id="element-1631">
                  <ShieldIcon className="h-5 w-5 text-yellow-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-1632" />
                  <div data-id="element-1633">
                    <p className="text-sm font-medium" data-id="element-1634">
                      Additional Verification Required
                    </p>
                    <p className="text-xs text-muted-foreground mt-1" data-id="element-1635">
                      {riskData.recommendation.action.replace('_', ' ')}:{' '}
                      {riskData.recommendation.details}
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row justify-center space-y-3 sm:space-y-0 sm:space-x-4 mt-6" data-id="element-1636">
                <Button variant="outline" data-id="element-1637">
                  <DownloadIcon className="mr-2 h-4 w-4" data-id="element-1638" />
                  Download Report
                </Button>
                <Button variant="outline" className="flex items-center" data-id="element-1639">
                  <Clock8Icon className="mr-2 h-4 w-4" data-id="element-1640" />
                  Request Approval
                </Button>
                <Button variant="destructive" data-id="element-1641">
                  <XCircleIcon className="mr-2 h-4 w-4" data-id="element-1642" />
                  Reject Device
                </Button>
                <Button data-id="element-1643">
                  <CheckCircleIcon className="mr-2 h-4 w-4" data-id="element-1644" />
                  Proceed with Acquisition
                </Button>
              </div>
            </div>}
        </CardContent>
        <CardFooter className="border-t flex justify-between" data-id="element-1645">
          <Button variant="outline" onClick={handlePreviousStep} disabled={currentStep === 1} data-id="element-1646">
            Back
          </Button>
          <Button onClick={handleNextStep} disabled={currentStep === 3 && !analysisComplete || currentStep === 4 && !riskAssessmentComplete || currentStep === steps.length} data-id="element-1647">
            {currentStep < steps.length ? <>
                Next <ArrowRightIcon className="ml-2 h-4 w-4" data-id="element-1648" />
              </> : 'Finish'}
          </Button>
        </CardFooter>
      </Card>
    </div>;
};
export default PhoneGrading;